import { useEffect, useRef, useState } from "react";

// Reconhecimento de voz do próprio navegador (Web Speech API). Funciona no Chrome, Edge e
// Safari; no Firefox não existe, e o botão de microfone não aparece. No Chrome o áudio é
// transcrito pelos servidores do Google.
type Reconhecimento = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((evento: any) => void) | null;
  onerror: ((evento: any) => void) | null;
  onend: (() => void) | null;
};

// Lido na hora de usar (e não ao carregar o arquivo), para refletir o navegador atual.
const obterConstrutor = (): (new () => Reconhecimento) | undefined =>
  (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

export type ErroDitado = "sem_permissao" | "sem_audio" | "falhou" | null;

interface CallbacksDitado {
  // A cada trecho reconhecido (parcial), para o texto ir aparecendo no campo.
  aoTranscrever: (texto: string) => void;
  // Quando a pessoa para de falar (ou toca em parar) e há texto: envio automático.
  aoFinalizar: (texto: string) => void;
}

export const useDitado = (idioma: "pt" | "es", callbacks: CallbacksDitado) => {
  const [ouvindo, setOuvindo] = useState(false);
  const [erro, setErro] = useState<ErroDitado>(null);
  const reconhecimento = useRef<Reconhecimento | null>(null);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  useEffect(() => () => reconhecimento.current?.abort(), []);

  const iniciar = (textoAtual: string) => {
    const Construtor = obterConstrutor();
    if (!Construtor) return;
    const r = new Construtor();
    r.lang = idioma === "es" ? "es-MX" : "pt-BR";
    r.interimResults = true;
    // false: o navegador encerra sozinho quando a pessoa faz uma pausa, e aí enviamos.
    r.continuous = false;

    const prefixo = textoAtual.trim() ? `${textoAtual.trim()} ` : "";
    let texto = "";
    let cancelado = false;
    let falhou = false;

    r.onresult = (evento) => {
      const falado = Array.from(evento.results as ArrayLike<any>)
        .map((resultado) => resultado[0].transcript)
        .join("")
        .trim();
      texto = falado ? prefixo + falado : "";
      if (texto) callbacksRef.current.aoTranscrever(texto);
    };
    r.onerror = (evento) => {
      if (evento.error === "aborted") return; // cancelamento pelo Esc
      falhou = true;
      setErro(
        evento.error === "not-allowed" || evento.error === "service-not-allowed"
          ? "sem_permissao"
          : evento.error === "no-speech"
          ? "sem_audio"
          : "falhou"
      );
    };
    r.onend = () => {
      setOuvindo(false);
      reconhecimento.current = null;
      if (!cancelado && !falhou && texto) callbacksRef.current.aoFinalizar(texto);
    };

    // Guardado no próprio objeto para o cancelar() marcar esta sessão de ditado.
    (r as any).cancelar = () => {
      cancelado = true;
      r.abort();
    };

    setErro(null);
    setOuvindo(true);
    reconhecimento.current = r;
    try {
      r.start();
    } catch {
      setOuvindo(false);
      setErro("falhou");
    }
  };

  // Parar = "terminei de falar": o onend dispara e o texto é enviado.
  const parar = () => reconhecimento.current?.stop();

  // Cancelar (Esc) descarta o que foi ditado e não envia.
  const cancelar = () => (reconhecimento.current as any)?.cancelar?.();

  return { disponivel: Boolean(obterConstrutor()), ouvindo, erro, iniciar, parar, cancelar };
};
